// src/pages/Admin/ProgramCategoriesView.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import PaginatedTable from '../../components/PaginatedTable';
import TitleWithBack from '../../components/common/TitleWithBack';

function ProgramCategoriesView() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [tooltip, setTooltip] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
  }, [sortOrder]);

  const fetchCategories = () => {
    setLoading(true);
    axios.get('/api/program-categories')
      .then((response) => {
        let data = response.data;
        data.sort((a, b) =>
          sortOrder === 'asc'
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name)
        );
        setCategories(data);
        setLoading(false);
      })
      .catch(() => {
        setError('Error fetching program categories.');
        setLoading(false);
      });
  };

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const handleEdit = (id) => {
    navigate(`/admin/program-categories/edit/${id}`);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/program-categories/${id}`);
      setTooltip('Category deleted successfully!');
      fetchCategories();
      setDeleteId(null);
    } catch (err) {
      setTooltip('Error deleting category.');
    }
  };

  const filteredCategories = categories.filter((cat) =>
    cat.name.toLowerCase().includes(filter.toLowerCase())
  );

  const columns = [
    { key: 'serial', header: <>#</> },
    { key: 'name', header: <>Name</> },
    { key: 'code', header: <>Code</> },
    { key: 'description', header: <>Description</> },
    { key: 'actions', header: 'Actions' }
  ];

  const renderRow = (cat, idx, pageIndex, pageSize) => (
    <>
      <td>{(pageIndex * pageSize) + idx + 1}</td>
      <td>{cat.name}</td>
      <td>{cat.code}</td>
      <td>{cat.description}</td>
      <td>
        <button className="btn btn-sm btn-primary me-2" onClick={() => handleEdit(cat.id)}>
          Edit
        </button>
        <button className="btn btn-sm btn-danger" onClick={() => setDeleteId(cat.id)}>
          Delete
        </button>
      </td>
    </>
  );

  return (
    <div className="container-fluid mt-2 px-4">
      <TitleWithBack title="View Program Categories" />
      <div className="d-flex mb-3">
        <input
          type="text"
          className="form-control me-2"
          placeholder="Search by category name..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <button className="btn btn-secondary" onClick={toggleSortOrder}>
          Sort {sortOrder === 'asc' ? 'Descending' : 'Ascending'}
        </button>
      </div>
      {loading ? (
        <p>Loading categories...</p>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : filteredCategories.length === 0 ? (
        <p>Nothing to show here</p>
      ) : (
        <PaginatedTable
          data={filteredCategories}
          columns={columns}
          renderRow={renderRow}
          rowsPerPage={50}
        />
      )}
      {tooltip && (
        <div className={`mt-3 alert ${tooltip.startsWith('Error') ? 'alert-danger' : 'alert-success'}`}>
          {tooltip}
        </div>
      )}
      {deleteId && (
        <ConfirmationModal 
          title="Confirm Delete"
          message="Are you sure you want to delete this category?"
          onConfirm={() => handleDelete(deleteId)}
          onCancel={() => setDeleteId(null)}
        />
      )}
    </div>
  );
}

export default ProgramCategoriesView;
